import { useCallback, useEffect, useRef, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { useFormikContext } from "formik";
import useDragAndDrop, { DragAndDrop } from "../../hooks/useDragAndDrop";
import useLatestRef from "../../hooks/useLatestRef";
import Container from "../container";
import SelectAll from "../selects/selectAll";
import SelectAllMultiple from "../selects/selectAllMultiple";
import idGen from "../../utils/idGen";
import { db } from "../../db";
import Conditions from "../conditionsBox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCodeCommit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useDataSettings } from "../../hooks/useData";
import { SynthKeySelect, SynthValuesSelect } from "../pipelines/selects";
import AndOrSelector from "../query/andOrSelector";
import FromCatalog from "./fromCatalog";
import styles from "./styles.module.scss";

const quote = (value, type) => {
  if (type === "number") return Number(value);
  return value.includes('"') ? value : `"${value}"`;
};

const idsOf = (dynamic) => (dynamic ?? []).map((pipe) => pipe?.id || undefined);
const countIds = (ids) => {
  const count = new Map();
  for (const id of ids) if (id !== undefined) count.set(id, (count.get(id) ?? 0) + 1);
  return count;
};

// The keys of the rows of the dynamic pipelines, so that an add, a delete, a
// move or a change of pipeline in the select of a row remounts neither the
// other rows nor that row: a remount drops the pending edit of the
// conditions box of the row (and its unmount enables the Save it disabled).
// A row keeps its key of the previous render (`previous`: { dynamic, keys }),
// found by the id of its pipeline (one per loader), or, when its select
// changed that id, by its index (the same rows, the id at that index
// replaced by one no row had). Otherwise the key is the id of its pipeline;
// for a pipeline not chosen yet (Add: no id) or an id of several rows, its
// index. Only the rendering: nothing is added to the rows.
export function pipeKeys(dynamic, previous) {
  const ids = idsOf(dynamic);
  const count = countIds(ids);
  const unique = (id) => id !== undefined && count.get(id) === 1;

  const before = idsOf(previous?.dynamic);
  const countBefore = countIds(before);
  const keyBefore = new Map();
  before.forEach((id, index) => {
    if (id !== undefined && countBefore.get(id) === 1)
      keyBefore.set(id, previous.keys?.[index]);
  });
  const renamed = (index) =>
    before.length === ids.length &&
    unique(ids[index]) &&
    !countBefore.has(ids[index]) &&
    keyBefore.has(before[index]) &&
    !count.has(before[index]);

  const keys = [];
  const used = new Set();
  ids.forEach((id, index) => {
    let key;
    if (keyBefore.has(id)) {
      if (unique(id)) key = keyBefore.get(id);
    } else if (renamed(index)) key = keyBefore.get(before[index]);
    if (key !== undefined && !used.has(key)) {
      keys[index] = key;
      used.add(key);
    }
  });
  ids.forEach((id, index) => {
    if (keys[index] !== undefined) return;
    const base = unique(id) ? `id:${id}` : `index:${index}`;
    let key = base;
    for (let n = 2; used.has(key); n++) key = `${base}#${n}`;
    keys[index] = key;
    used.add(key);
  });
  return keys;
}

// The index of the dynamic pipeline `pipeId` in the values, wherever it is
// now (`hint`: its index at the last render of its row, for a repeated id);
// -1 if it is gone.
export function pipeIndex(values, pipeId, hint) {
  const dynamic = values?.pipelines?.dynamic;
  if (!pipeId || !Array.isArray(dynamic)) return -1;

  return dynamic[hint]?.id === pipeId
    ? hint
    : dynamic.findIndex((pipe) => pipe?.id === pipeId);
}

// The values with `condition` on the dynamic pipeline `pipeId` (pipeIndex),
// and lastSaved.date touched; null if it is gone. The touch: the box
// changes the condition in place (the and/or arrays, for an edit of a
// value), so when the values share their objects with the initial values
// (after a reset of the form, as a Save does) the edit is in both and the
// form would not be dirty, Save disabled.
export function withPipeCondition(values, pipeId, condition, hint) {
  const index = pipeIndex(values, pipeId, hint);
  if (index < 0) return null;

  const dynamic = values.pipelines.dynamic;

  const next = dynamic.slice();
  next[index] = { ...dynamic[index], condition };
  return {
    ...values,
    pipelines: { ...values.pipelines, dynamic: next },
    lastSaved: { ...values.lastSaved, date: new Date().toISOString() }
  };
}

export default function Pipelines(props) {
  const showDynamic = props.showDynamic ?? true;
  // loaders only: the meta packs already read their documents from a catalog
  const showFromCatalog = props.showFromCatalog ?? false;
  const { values, errors, setFieldValue } = useFormikContext();
  // The values of the last render, for the hand-off of a row's box (Pipeline):
  // also when the row is unmounted (a fold of Dynamic) and no longer renders
  const latestValues = useLatestRef(values);
  const operatori = useDataSettings("operators");

  const operatorsFilter = useCallback(() => {
    return [operatori, false];
  }, [operatori]);

  const addPipe = useCallback(() => {
    const pipes = values.pipelines?.dynamic ? values.pipelines.dynamic.slice() : [];
    pipes.unshift({
      condition: {
        and: [
          {
            operator: "*",
            values: [
              {
                type: "metadata",
                value: "",
              },
              {
                type: "",
                value: "",
              },
            ],
            id: idGen(),
          },
        ],
      },
    });

    setFieldValue("pipelines.dynamic", pipes);
  }, [setFieldValue, values.pipelines]);

  const onChangeStatic = useCallback(
    (pipes) => {
      setFieldValue("pipelines.static", pipes);
    },
    [setFieldValue]
  );

  const [filters, existingLen] = useMemo(() => {
    if (values.pipelines) {
      const statici = values.pipelines?.static ?? [];
      const dynamic = values.pipelines?.dynamic?.map?.(({ id }) => id).filter(Boolean) ?? [];
      const exclude = [...statici, ...dynamic];

      return [{ exclude }, exclude.length];
    }
    return [{ exclude: [] }, 0];
  }, [values.pipelines]);

  const canAdd = useMemo(() => {
    if (existingLen) {
      const pipes = db.getTable("pipelines");
      if (pipes.size === existingLen) return false;
    }

    return true;
  }, [existingLen]);

  // The rows and their keys of the last render committed (pipeKeys)
  const dynamic = values.pipelines?.dynamic;
  const previousKeys = useRef();
  const keys = useMemo(
    () => pipeKeys(dynamic, previousKeys.current),
    [dynamic]
  );
  useEffect(() => {
    previousKeys.current = { dynamic, keys };
  }, [dynamic, keys]);

  return (
    <Container
      title="Pipelines"
      help="loader-pipelines"
      folded={true}
      warningMessage={props.errori?.pipelines || errors.pipelines}
      headerOnlyOnFolded={false}
      color={props.color}
      coloredBars={props.coloredBars}
      extra={
        props.extra || (
          <Form.Check
            type="switch"
            value="true"
            checked={values.pipelines?.active ?? false}
            onChange={(e) => setFieldValue("pipelines.active", e.target.checked)}
            id="activate-pipelines"
            name="activate-pipelines"
            label="Active"
            className={styles.switch}
          />
        )
      }
    >
      <Container
        title="Static"
        help="loader-static-pipeline"
        headerOnlyOnFolded={false}
        header={<AndOrSelector defaultOperator="and" showTie={false} />}
        folded={true}
      >
        <SelectAllMultiple
          accessor="pipelines"
          filters={filters}
          handleChange={onChangeStatic}
          values={values.pipelines?.static}
        />
      </Container>
      {showFromCatalog && <FromCatalog />}
      {showDynamic && (
        <Container
          title="Dynamic"
          help="loader-dynamic-pipeline"
          headerOnlyOnFolded={false}
          header={<AndOrSelector defaultOperator="or" showTie={false} />}
          folded={true}
          footer={
            <>
              {canAdd && (
                <div className={styles.pipeFooter}>
                  <Button variant="outline-secondary" size="sm" onClick={addPipe}>
                    Add Pipeline
                  </Button>
                </div>
              )}
            </>
          }
        >
          <DragAndDrop>
            {values.pipelines?.dynamic?.map?.((pipe, index) => (
              <Pipeline
                index={index}
                pipe={pipe}
                latestValues={latestValues}
                key={keys[index]}
                filters={filters}
                operatorsFilter={operatorsFilter}
                getKeyComponent={props.getKeyComponent}
                getValueComponent={props.getValueComponent}
              />
            ))}
          </DragAndDrop>
        </Container>
      )}
    </Container>
  );
}

function Pipeline(props) {
  const { index, pipe, filters, operatorsFilter, latestValues } = props;
  const ref = useRef();
  const { values, setFieldValue, setValues } = useFormikContext();
  const { opacity, highlighted, hovered } = useDragAndDrop(
    ref,
    "pipelines",
    index,
    "pipeline",
    values.pipelines?.dynamic ?? [],
    (_, data) => setFieldValue("pipelines.dynamic", data)
  );

  const stile = useMemo(() => {
    const style = { opacity };

    if (highlighted && !hovered) {
      style.borderColor = "deepskyblue";

      return style;
    }
    if (hovered) {
      style.borderColor = "darkorange";
      style.borderBox = "4px 4px 8px deeppink";
      style.marginTop = "20px";
      style.marginBottom = "20px";
      style.opacity = 0.2;

      return style;
    }

    return style;
  }, [highlighted, hovered, opacity]);

  const setPipe = useCallback(
    (id) => {
      setFieldValue(`pipelines.dynamic[${index}].id`, id);
    },
    [index, setFieldValue]
  );

  // The conditions box hands the edit over 1 s after it (debounced), through
  // this callback as it was at the edit: by then the pipeline may be at
  // another index (Add puts the new one first, a delete or a move shift the
  // others) or gone, and its row may be unmounted (a fold of the boxes
  // around it). So the pipeline is found by id among the values of the last
  // render of the form (Formik resolves the function then), not by the index
  // of the edit; nothing is written if it has been removed. The id is the
  // one of the last render of the row: its select may have changed it since
  // the edit (the row keeps its key, pipeKeys, and stays mounted); once the
  // row is unmounted, its last one (deleted: found nowhere). When it is gone
  // from the values of the last render, nothing is written at all: a write
  // of the same values still re-renders and validates the form.
  const latestId = useLatestRef(pipe.id);
  const latestIndex = useLatestRef(index);
  const onChangeCondition = useCallback(
    ({ conditionRaw }) => {
      const id = latestId.current;
      const hint = latestIndex.current;
      if (pipeIndex(latestValues.current, id, hint) < 0) return;

      setValues(
        (values) => withPipeCondition(values, id, conditionRaw, hint) ?? values
      );
    },
    [latestId, latestIndex, latestValues, setValues]
  );

  const getKeyComponent = useCallback(
    ({ value, handleChangeValue }) => {
      if (props.getKeyComponent) return props.getKeyComponent({ value, handleChangeValue });

      return (
        <SynthKeySelect
          value={value}
          className={styles.keySelect}
          handleChange={handleChangeValue}
          valori={db.get("pipelines", pipe.id)}
        />
      );
    },
    [pipe.id, props]
  );

  const getValueComponent = useCallback(
    ({ field, value, handleChangeValue }) => {
      const change = (v) => handleChangeValue(quote(v));

      if (props.getValueComponent)
        return props.getValueComponent({ field, value, handleChangeValue: change });

      return (
        <SynthValuesSelect
          className={styles.keySelect}
          handleChange={change}
          value={value}
          chiave={field}
          valori={db.get("pipelines", pipe.id)}
        />
      );
    },
    [pipe.id, props]
  );

  const deletePipe = useCallback(() => {
    const pipes = values.pipelines.dynamic.slice();
    pipes.splice(index, 1);
    setFieldValue("pipelines.dynamic", pipes);
  }, [index, setFieldValue, values.pipelines?.dynamic]);

  return (
    <Container
      title={<FontAwesomeIcon icon={faCodeCommit} className={styles.pipeIcon} />}
      coloredBars={false}
      header={
        <SelectAll
          accessor="pipelines"
          filters={filters}
          handleChange={setPipe}
          value={pipe.id ?? ""}
        />
      }
      extra={
        <FontAwesomeIcon icon={faTrashAlt} className={styles.deleteIcon} onClick={deletePipe} />
      }
      ref={ref}
      style={stile}
      headerOnlyOnFolded={false}
      folded={true}
      // A boolean: Container folds again when foldable changes, and a change
      // of pipeline in the select would fold the row, unmounting its box
      // (and its pending edit, see onChangeCondition)
      foldable={Boolean(pipe.id)}
    >
      <Conditions
        conditionRaw={pipe.condition}
        onChangeCondition={onChangeCondition}
        getKeyComponent={getKeyComponent}
        getValueComponent={getValueComponent}
        operatorsFilter={operatorsFilter}
        enableReinitialize={true}
        defaultOperator={""}
      />
    </Container>
  );
}
